import React, {useEffect, useState} from 'react';
import API from '../../../app/api';
import {useDispatch} from 'react-redux';
import {setPatient} from '../../../store/reducers/patient';
import _ from 'lodash';
import InformationBox from '../../../components/other/informationBox';
import {useTranslation} from 'react-i18next';
import AgyleLoadingSpinner from '../../../components/other/agyleLoadingSpinner';
import clsx from 'clsx';
import AgyleToggleButtons from '../../../components/ui/buttons/agyleToggleButtons';
import {AgyleNamedIcon} from '../../../components/icons/agyle';
import ActivitySpellSelector from '../../../components/notes/component/activitySpellSelector';
import HistAttendanceField from './histAttendanceField';
import {setCurrentTab, setHistoryPatient} from '../../../store/reducers/patientContext';
import {sendUserAudit} from '../../../store/reducers/user';
import {AUDIT_TYPES} from '../../../util/auditUtil';
import {useHistStyles} from './useHistStyles';
import {BASE_TAB} from '../../../util/patientContextUtil';
import useDict from '../../../hooks/useDict';

function InpatientHistoryList({patient}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const classes = useHistStyles();

    // Dict
    const [hospitals] = useDict('hospitals');

    const admissionId = patient?.admission?.__id;

    const [attendances, setAttendances] = useState([]);

    // Filters
    const [expandAll, setExpandAll] = useState(false);
    const [selectedAttendance, setSelectedAttendance] = useState(null);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAttendance();
    }, [patient]);

    const loadAttendance = () => {
        API.getPatientAttendancesHistory(patient?.patient?.__id)
            .then((result) => {
                setAttendances(result);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const openItem = (param, disabled) => {
        if (!disabled && param.patient) {
            dispatch(sendUserAudit(param, AUDIT_TYPES.OPEN_HISTORICAL_ACTIVITY));
            dispatch(setPatient(param.patient));
            dispatch(setHistoryPatient(param));
            dispatch(setCurrentTab(BASE_TAB));
        }
    };

    const getFilteredAtt = () => {
        if (selectedAttendance) {
            return _.filter(attendances, att => att?.admission?.__id === selectedAttendance?.__id) || [];
        }
        return attendances;
    };

    return (
        <div className={classes.root}>
            <div className={classes.panel}>
                <div>
                    <ActivitySpellSelector
                        type="activity"
                        admissions={attendances?.map(n => n.admission)}
                        setCurrentActivity={setSelectedAttendance}
                    />
                </div>

                <div style={{width: 100}}>
                    <AgyleToggleButtons
                        classNameRoot={clsx(classes.toggleRoot, 'header')}
                        classNameLabel={clsx(classes.toggleLabel, 'icon')}
                        classNameSelected={classes.toggleSelected}
                        onChange={(val) => {
                            if (_.isBoolean(val)) {
                                setExpandAll(val);
                            }
                        }}
                        value={expandAll}
                        size="small"
                        options={[
                            {
                                value: false,
                                label: <AgyleNamedIcon className={classes.icon} name="collapseAll"/>,
                            },
                            {
                                value: true,
                                label: <AgyleNamedIcon className={classes.icon} name="expandAll"/>,
                            },
                        ]}
                        fullWidth
                    />
                </div>
            </div>

            {loading && <AgyleLoadingSpinner/>}

            {!loading && _.isEmpty(attendances) &&
                <InformationBox type="info" style={{margin: '0 10px', width: 'auto'}}>
                    {t('This patient has no historical activities.')}
                </InformationBox>
            }

            <div className={clsx(classes.list, 'scroll-normal-white')}>
                {!loading && getFilteredAtt()?.map(a => (
                    <HistAttendanceField
                        key={a.admission.__id}
                        admission={a}
                        expandFromParent={expandAll}
                        onOpen={openItem}
                        disabledAdmission={admissionId === a.admission.__id}
                        hospitals={hospitals}
                    />
                ))}
            </div>
        </div>
    );
}

export default InpatientHistoryList;